import type { MosaicNode } from "react-mosaic-component";
import type { TabsState } from "@/context/tabs/types";
import type { ViewId } from "@/shared/types/common";

export const replaceMosaicNode = (
	mosaicState: MosaicNode<ViewId>,
	targetId: string,
	replacementId: ViewId,
) => {
	const updateNode = (
		node: MosaicNode<ViewId>,
		targetId: string,
		replacementId: ViewId,
	): MosaicNode<ViewId> => {
		if (!node) return node;

		if (typeof node === "string" && node === targetId) {
			return replacementId;
		}

		if (typeof node === "object") {
			if (node.first === targetId) return { ...node, first: replacementId };
			if (node.second === targetId) return { ...node, second: replacementId };
			return {
				...node,
				first: updateNode(node.first, targetId, replacementId),
				second: updateNode(node.second, targetId, replacementId),
			};
		}

		return node;
	};

	return updateNode(mosaicState, targetId, replacementId);
};

export const mosaicIncludesNode = (
	mosaicState: MosaicNode<ViewId>,
	targetId: string,
): boolean => {
	const searchNode = (node: MosaicNode<ViewId>, targetId: string): boolean => {
		if (!node) return false;

		if (typeof node === "string") {
			return node === targetId;
		}

		if (typeof node === "object") {
			return (
				searchNode(node.first, targetId) || searchNode(node.second, targetId)
			);
		}

		return false;
	};

	return searchNode(mosaicState, targetId);
};

export const getWidgetIdsFromLayout = (
	layout: MosaicNode<ViewId> | null,
): string[] => {
	if (typeof layout === "string") {
		return [layout];
	}

	if (typeof layout === "object" && layout !== null) {
		return [
			...(layout.first ? getWidgetIdsFromLayout(layout.first) : []),
			...(layout.second ? getWidgetIdsFromLayout(layout.second) : []),
		];
	}

	return [];
};

/**
 * Determines if a mosaic tile should be draggable based on the current tab.
 * For default tabs, the draggable state is not allowed.
 * For custom tabs, dragging is always allowed.
 *
 * @param tabs - The current state of all tabs
 * @param activeTab - The ID of the currently active tab
 * @returns Boolean indicating whether the mosaic tile should be draggable
 */
export function isMosaicTileDraggable({
	tabs,
	activeTab,
}: {
	tabs: TabsState;
	activeTab: string;
}): boolean {
	const isDefaultTab =
		tabs.find((t) => t.id === activeTab && t.isDefault) !== undefined;

	return !isDefaultTab;
}
